import React, { useEffect } from 'react';
import { useSearchParams, useParams, useLocation } from 'react-router-dom';

const SITE_URL = 'https://skore.info';
const SITE_ORGANIZATION_ID = `${SITE_URL}/#organization`;
const SITE_LOGO_URL = `${SITE_URL}/opengraph-image.png`;

interface Game {
  id: string;
  sport: string;
  homeTeam: string;
  awayTeam: string;
  date: string;
  competition?: string;
  homeScore?: number;
  awayScore?: number;
}

interface StructuredDataManagerProps {
  currentSport?: 'NBA' | 'NFL' | 'MLB' | 'NHL' | 'Soccer';
  games?: Game[];
  pageType?: 'home' | 'sport' | 'blog' | 'about';
  blogPost?: {
    title: string;
    description: string;
    author: string;
    datePublished: string;
    dateModified?: string;
    image?: string;
  };
}

const StructuredDataManager: React.FC<StructuredDataManagerProps> = ({ 
  currentSport,
  games = [],
  pageType = 'home',
  blogPost,
}) => {
  const [searchParams] = useSearchParams();
  const { competition: competitionParam } = useParams<{ competition?: string }>();
  const location = useLocation();
  
  useEffect(() => {
    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"].structured-data-dynamic');
    existingScripts.forEach(script => script.remove());

    const scripts: HTMLScriptElement[] = [];

    // Helper function to create structured data script
    const createStructuredDataScript = (data: object, id: string) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.className = 'structured-data-dynamic';
      script.id = id;
      script.textContent = JSON.stringify(data, null, 2);
      return script;
    };

    // Base WebSite schema (always include)
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "skore.info",
      "alternateName": "Skore",
      "url": SITE_URL,
      "description": "Watch sports without spoilers. Filter games by excitement level without seeing scores.",
      "publisher": {
        "@type": "Organization",
        "@id": SITE_ORGANIZATION_ID
      }
    };

    // Organization schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": SITE_ORGANIZATION_ID,
      "name": "skore.info",
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": SITE_LOGO_URL,
        "width": 1024,
        "height": 1024
      },
      "foundingDate": "2024",
      "description": "A platform for watching sports without spoilers",
      "sameAs": [
        "https://twitter.com/skore_info"
      ]
    };

    // SportsApplication schema
    const sportsAppSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "skore.info",
      "applicationCategory": "SportsApplication",
      "operatingSystem": "Web Browser",
      "url": SITE_URL,
      "description": "Watch sports without spoilers. Filter games by excitement level without seeing scores.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      },
      "applicationSubCategory": "Sports Data",
      "downloadUrl": SITE_URL,
      "featureList": [
        "Spoiler-free sports viewing",
        "Game excitement filtering",
        "Multiple sports coverage",
        "Recent game data"
      ]
    };

    // Add base schemas
    scripts.push(createStructuredDataScript(websiteSchema, 'website-schema'));
    scripts.push(createStructuredDataScript(organizationSchema, 'organization-schema'));
    scripts.push(createStructuredDataScript(sportsAppSchema, 'sports-app-schema'));

    // Sport-specific schemas
    if (currentSport && pageType === 'sport') {
      const sportMapping = {
        NBA: { name: "Basketball", league: "National Basketball Association" },
        NFL: { name: "American Football", league: "National Football League" },
        MLB: { name: "Baseball", league: "Major League Baseball" },
        NHL: { name: "Ice Hockey", league: "National Hockey League" },
        Soccer: { name: "Soccer", league: "Professional Soccer" }
      };

      const sportInfo = sportMapping[currentSport];
      
      const sportsOrganizationSchema = {
        "@context": "https://schema.org",
        "@type": "SportsOrganization",
        "name": sportInfo.league,
        "sport": sportInfo.name,
        "url": `https://skore.info/sport/${currentSport.toLowerCase()}`,
        "description": `${sportInfo.name} games and scores on skore.info`,
        "parentOrganization": {
          "@type": "Organization",
          "@id": "https://skore.info/#organization"
        }
      };

      scripts.push(createStructuredDataScript(sportsOrganizationSchema, 'sports-org-schema'));

      // Individual game events
      if (games.length > 0) {
        const gameEvents = games.slice(0, 10).map((game, index) => {
          const eventName = `${game.awayTeam} vs ${game.homeTeam}${game.competition ? ` - ${game.competition}` : ''}`;
          const eventDescription = `${sportInfo.name} game: ${game.awayTeam} at ${game.homeTeam}${game.competition ? ` (${game.competition})` : ''}. Scheduled for ${new Date(game.date).toLocaleDateString()}.`;
          
          return {
            "@context": "https://schema.org",
            "@type": "SportsEvent",
            "name": eventName,
            "description": eventDescription,
            "startDate": game.date,
            "eventStatus": "https://schema.org/EventScheduled", // Default status
            "sport": sportInfo.name,
            "homeTeam": {
              "@type": "SportsTeam",
              "name": game.homeTeam
            },
            "awayTeam": {
              "@type": "SportsTeam", 
              "name": game.awayTeam
            },
            "organizer": {
              "@type": "SportsOrganization",
              "name": game.competition || sportInfo.league // Use specific competition if available
            },
            "location": {
              "@type": "Place",
              "name": "Sports Venue" // Generic, could be enhanced if venue data is available
            },
            "url": `https://skore.info/sport/${currentSport.toLowerCase()}#game-${game.id}`,
            // Placeholder for VideoObject data. This would need to be populated from game object.
            // "video": game.highlightVideos ? game.highlightVideos.map(video => ({
            //   "@type": "VideoObject",
            //   "name": video.name,
            //   "description": video.description,
            //   "thumbnailUrl": video.thumbnailUrl,
            //   "uploadDate": video.uploadDate,
            //   "duration": video.duration, // ISO 8601 duration format e.g., PT1M30S
            //   "contentUrl": video.contentUrl,
            //   "embedUrl": video.embedUrl, // If embeddable
            //   "interactionStatistic": video.interactionStatistic ? {
            //     "@type": "InteractionCounter",
            //     "interactionType": { "@type": "WatchAction" },
            //     "userInteractionCount": video.interactionStatistic.viewCount
            //   } : undefined
            // })) : undefined,
            // Potential future enhancements:
            // "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            // "subEvent" (e.g., for different parts of a tournament)
            // "performer" (for individual sports)
          };
        });

        gameEvents.forEach((gameEvent, index) => {
          scripts.push(createStructuredDataScript(gameEvent, `game-event-${index}`));
        });
      }
    }

    // Blog post schema
    if (pageType === 'blog' && blogPost) {
      const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": blogPost.title,
        "description": blogPost.description,
        "author": {
          "@type": "Organization",
          "name": blogPost.author,
          "url": SITE_URL
        },
        "publisher": {
          "@type": "Organization",
          "@id": SITE_ORGANIZATION_ID
        },
        "datePublished": blogPost.datePublished,
        "dateModified": blogPost.dateModified || blogPost.datePublished,
        "url": `https://skore.info${location.pathname}`,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://skore.info${location.pathname}`
        },
        "articleSection": "Sports",
        "keywords": ["sports", "spoiler-free", currentSport].filter(Boolean).join(", ")
      };

      if (blogPost.image) {
        articleSchema["image"] = {
          "@type": "ImageObject",
          "url": blogPost.image,
          "width": 1200,
          "height": 630
        };
      }

      scripts.push(createStructuredDataScript(articleSchema, 'article-schema'));
    }

    // Breadcrumb navigation
    if (pageType === 'sport' && currentSport) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://skore.info"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": currentSport,
            "item": `https://skore.info/sport/${currentSport.toLowerCase()}`
          }
        ]
      };

      if (competitionParam) {
        breadcrumbSchema.itemListElement.push({
          "@type": "ListItem",
          "position": 3,
          "name": competitionParam,
          "item": `https://skore.info/sport/${currentSport.toLowerCase()}/${competitionParam}`
        });
      }

      scripts.push(createStructuredDataScript(breadcrumbSchema, 'breadcrumb-schema'));
    }

    // Add all scripts to document head
    scripts.forEach(script => {
      document.head.appendChild(script);
    });

    // Cleanup function
    return () => {
      scripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [currentSport, pageType, JSON.stringify(games), JSON.stringify(blogPost), competitionParam, location.pathname]);

  return null;
};

export default StructuredDataManager;
